import React from 'react'
import useSWR from 'swr'

import { flexFetcher, type FlexQueryInput, type FlexResponse } from '@library/api-menu-experience'

import type { FlexPortionEditorState } from '../components/FlexPortionEditor'

type FlexPortionsRequest = FlexQueryInput & { isOpen: boolean }

export type FlexPortionsSWRKey = {
  key: 'menu-flex-portions'
  contextId: string
  input: FlexQueryInput
}

const getContextId = (input: FlexQueryInput) => JSON.stringify(input)

export const getFlexPortionsSWRKey = (request: FlexPortionsRequest): FlexPortionsSWRKey | null => {
  if (!request.isOpen) {
    return null
  }

  const recipeIdentifier =
    request.recipeUrn !== undefined
      ? { recipeUrn: request.recipeUrn }
      : { recipeId: request.recipeId }
  const menuIdentifier =
    request.menuUrn !== undefined ? { menuUrn: request.menuUrn } : { menuId: request.menuId }
  const input: FlexQueryInput = {
    ...recipeIdentifier,
    ...menuIdentifier,
    numPortions: request.numPortions,
    ...(request.deliveryDate && { deliveryDate: request.deliveryDate }),
  }

  return {
    key: 'menu-flex-portions',
    contextId: getContextId(input),
    input,
  }
}

type FlexPortionsResult = {
  contextId: string
  response: FlexResponse
}

const fetchFlexPortions = async ({
  contextId,
  input,
}: FlexPortionsSWRKey): Promise<FlexPortionsResult> => ({
  contextId,
  response: await flexFetcher(input),
})

export const useFlexPortions = (request: FlexPortionsRequest): FlexPortionEditorState => {
  const key = getFlexPortionsSWRKey(request)
  const { data, error, mutate } = useSWR(key, fetchFlexPortions, {
    keepPreviousData: true,
    shouldRetryOnError: false,
  })
  const isCurrentContext = key !== null && data?.contextId === key.contextId
  const retry = React.useCallback(() => {
    void mutate()
  }, [mutate])

  if (error) {
    return { status: 'error', onRetry: retry }
  }

  if (!isCurrentContext) {
    return { status: 'loading' }
  }

  return { status: 'success', portions: data.response.portions }
}
